import { Component, TemplateFunction } from '@ribajs/core'
import type { SibSubscribeResponse } from '../types/index.js'

/**
 * The type for the scope
 */
interface Scope {
  // Properties

  feedback: {
    success: boolean
    successMessage: string
    error: boolean
    errorMessage: string
  }
  showForm: boolean
  loading: boolean
  email?: string
  givenname?: string
  familyname?: string
  allowRedirect: boolean

  // Methods

  onSubmit: SibNewsletterComponent['onSubmit']

  // Elements
  formEl?: HTMLFormElement
}

/**
 * A newsletter component for SendInBlue /Brevo
 */
export class SibNewsletterComponent extends Component {
  /**
   * The tag name of the element, in this case `<sib-newsletter>...</sib-newsletter>`
   */
  public static tagName = 'sib-newsletter'

  /**
   * Everything that is assigned to the scope can be used within the template with the Riba template engine.
   * Examples:
   * - Methods can be called with `<div rv-on-click="show"></div>`, this will call the show method defined in the scope
   * - Properties can be used with `<div rv-click-active="visible"></div>`, this will add the class `active` if visible is `true` and remove the class `active` if visible is `false`,
   * - Templates can be used with `<div rv-template="body"></div>`, `<div rv-html="body"></div>` or `<div rv-text="body"></div>`, this will be add the template, html or string to the innerHTML of the element. But you can only use the Riba Template Engine with `rv-template`
   */
  public scope: Scope = {
    // Properties

    feedback: {
      success: false,
      successMessage: '',
      error: false,
      errorMessage: '',
    },
    showForm: true,
    loading: false,
    email: '',
    givenname: '',
    familyname: '',
    allowRedirect: false,

    // Methods

    onSubmit: this.onSubmit,
  }

  /**
   * Attributes that can be passed to the custom element.
   * @note These are automatically added to the scope and parsed to string, number, boolean, object, etc
   */
  static get observedAttributes(): string[] {
    return [
      'success-message-template',
      'error-message-template',
      'allow-redirect',
      'givenname',
      'familyname',
      'email',
    ]
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SibNewsletterComponent.observedAttributes)
  }

  async afterBind() {
    await super.afterBind()
    this.classList.add(`${SibNewsletterComponent.tagName}-ready`)
  }

  /**
   * Submit the form by hand by calling this method
   */
  public async submit() {
    if (!this.scope.formEl) {
      throw new Error('formEl is not defined')
    }
    try {
      // this.scope.formEl.submit()
      this.scope.formEl.dispatchEvent(new Event('submit', { cancelable: true }))
    } catch (error) {
      console.error(error)
    }

    return new Promise<SibSubscribeResponse>((resolve, reject) => {
      this.addEventListener(
        'sib-newsletter-submit:done',
        event => {
          const newsletterResult = (
            event as CustomEvent<SibSubscribeResponse | null>
          ).detail
          if (newsletterResult) {
            resolve(newsletterResult)
          } else {
            reject(newsletterResult)
          }
        },
        { once: true },
      )
    })
  }

  /** Called on submit, see `rv-on-submit="onSubmit"` in the template */
  public async onSubmit(event: Event, scope: Scope, form: HTMLFormElement) {
    if (event.type === 'submit') {
      // If the validation fails and redirect is allowed, return true and the form is processed as standard
      if (
        this.scope.formEl?.checkValidity() === false &&
        this.scope.allowRedirect
      ) {
        return true
      }
    }

    event?.preventDefault()

    this.scope.loading = true

    let result: SibSubscribeResponse
    try {
      const searchParam = new URLSearchParams([...(new FormData(form) as any)])
      const response = await fetch(form.action + '?isAjax=1', {
        method: form.method,
        body: searchParam,
      })

      result = await response.json()

      this.scope.loading = false
      this.scope.showForm = false

      if (result.success) {
        this.scope.feedback.success = true
        this.scope.feedback.successMessage = result.message || ''
      } else {
        this.scope.feedback.error = true
        this.scope.feedback.errorMessage = result.message || ''
      }

      this.dispatchEvent(
        new CustomEvent('sib-newsletter-submit:done', { detail: result }),
      )
    } catch (error: any) {
      console.warn('error', error)
      this.scope.loading = false
      this.scope.feedback.error = true
      this.scope.feedback.errorMessage = error?.message || ''
      this.dispatchEvent(
        new CustomEvent('sib-newsletter-submit:done', { detail: null }),
      )
    }
  }

  /**
   * Returns the template, if null is returned, the child html of this element / component is not replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
